import React, { useEffect, useState, useRef, useCallback } from 'react';
import Header from '../../../Header';
import Footer from '../../../Footer';
import { checkIsAdminLoggined } from '../../../../utils/utils';
import { adminProductApi } from '../../../../api/adminProductApi';
import { NUMBER_PER_PAGE } from '../../../pages/AdminProductsPage/constants';
import { parseApiError } from './BarcodeScanField';
import AdminBarcodeManagementDesktopView from './AdminBarcodeManagementDesktopView';
import AdminBarcodeManagementMobileView from './AdminBarcodeManagementMobileView';

const AdminBarcodeManagementPage = (props) => {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [quantityFrom, setQuantityFrom] = useState('');
  const [barcodeMode, setBarcodeMode] = useState('all');
  const [categoryIdsFilter, setCategoryIdsFilter] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stateFilter, setStateFilter] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const stateDropdownRefDesktop = useRef(null);
  const stateDropdownRefMobile = useRef(null);

  const [currentPage, setCurrentPage] = useState(0);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const pageCount = Math.max(1, Math.ceil(totalCount / NUMBER_PER_PAGE));

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    const qFrom =
      quantityFrom !== '' && quantityFrom != null ? Number(quantityFrom) : undefined;
    if (qFrom !== undefined && Number.isNaN(qFrom)) {
      setError('Некоректне значення «Кількість від»');
      setLoading(false);
      return;
    }
    const barcodePresent =
      barcodeMode === 'with' ? true : barcodeMode === 'without' ? false : undefined;
    adminProductApi
      .getProducts({
        state: stateFilter.length > 0 ? stateFilter : undefined,
        categoryIds: categoryIdsFilter.length > 0 ? categoryIdsFilter : undefined,
        quantityFrom: qFrom,
        barcodePresent,
        search: searchFilter != null && String(searchFilter).trim() !== '' ? String(searchFilter).trim() : undefined,
        page: currentPage + 1,
        numberPerPage: NUMBER_PER_PAGE,
        sortBy,
        sortOrder
      })
      .then((res) => {
        const list = res.data.list || res.data || [];
        setRows(list);
        setTotalCount(res.data.totalCount != null ? res.data.totalCount : list.length);
      })
      .catch((err) => {
        console.error(err);
        setRows([]);
        setTotalCount(0);
        setError(parseApiError(err) || 'Помилка завантаження');
      })
      .finally(() => setLoading(false));
  }, [currentPage, quantityFrom, barcodeMode, categoryIdsFilter, stateFilter, searchFilter, sortBy, sortOrder]);

  useEffect(() => {
    checkIsAdminLoggined(props);
  }, [props]);

  useEffect(() => {
    adminProductApi
      .getCategories()
      .then((res) => setCategories(res.data || []))
      .catch((err) => {
        console.error(err);
        setCategories([]);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onDoc = (e) => {
      const inDesktop =
        stateDropdownRefDesktop.current && stateDropdownRefDesktop.current.contains(e.target);
      const inMobile =
        stateDropdownRefMobile.current && stateDropdownRefMobile.current.contains(e.target);
      if (!inDesktop && !inMobile) {
        setShowStateDropdown(false);
      }
    };
    if (showStateDropdown) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [showStateDropdown]);

  const resetFilters = () => {
    setQuantityFrom('');
    setBarcodeMode('all');
    setCategoryIdsFilter([]);
    setStateFilter([]);
    setSearchFilter('');
    setSortBy('name');
    setSortOrder('asc');
    setCurrentPage(0);
  };

  const handlePageClick = (data) => {
    setCurrentPage(data.selected);
  };

  const handleSort = useCallback((field) => {
    if (field !== 'name' && field !== 'code') return;
    setCurrentPage(0);
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(field);
    setSortOrder('asc');
  }, [sortBy]);

  const handleRefresh = useCallback(() => {
    load();
  }, [load]);

  const onBarcodeSaved = useCallback((productId, barcode) => {
    setRows((prev) =>
      prev.map((r) => (r.id === productId ? { ...r, barcode } : r))
    );
    setError('');
  }, []);

  const onPackSaved = useCallback((productId, data) => {
    setRows(prev => prev.map(row => row.id === productId
      ? { ...row, packBarcode: data.packBarcode, packQuantity: data.packQuantity } : row));
    setError('');
  }, []);

  const onBarcodeError = useCallback((msg) => {
    setError(msg);
  }, []);

  const onCodeSaved = useCallback((productId, code) => {
    setRows((prev) =>
      prev.map((r) => (r.id === productId ? { ...r, code } : r))
    );
    setError('');
  }, []);

  const onCodeError = useCallback((msg) => {
    setError(msg);
  }, []);

  const viewProps = {
    quantityFrom,
    setQuantityFrom,
    searchFilter,
    setSearchFilter,
    barcodeMode,
    setBarcodeMode,
    categoryIdsFilter,
    setCategoryIdsFilter,
    categories,
    stateFilter,
    setStateFilter,
    showStateDropdown,
    setShowStateDropdown,
    resetFilters,
    error,
    loading,
    rows,
    pageCount,
    currentPage,
    handlePageClick,
    onBarcodeSaved,
    onPackSaved,
    onBarcodeError,
    onCodeSaved,
    onCodeError,
    setCurrentPage,
    sortBy,
    sortOrder,
    handleSort,
    handleRefresh
  };

  return (
    <div className='container'>
      <div className='row'>
        <div className='col p-0'>
          <Header />
        </div>
      </div>

      <div className='row'>
        <AdminBarcodeManagementDesktopView
          {...viewProps}
          stateDropdownRef={stateDropdownRefDesktop}
        />
        <AdminBarcodeManagementMobileView
          {...viewProps}
          stateDropdownRef={stateDropdownRefMobile}
        />
      </div>

      <div className='row'>
        <div className='col p-0'>
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default AdminBarcodeManagementPage;
